import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';

import { theme } from '_constants/theme';
import { hexToRGBA } from '_helpers/theme';

import Text from 'components/atomic/layouts/Text';

const TWrapper = styled.div`
  display: flex;
  flex-direction: column;

  .column-placeholder {
    height: 32px;
    border-radius: 12px;
    line-height: 32px;
    background: ${hexToRGBA(theme.colors.gray.secondary, 0.5)};
    width: 100%;
    opacity: 0.2;
  }
`;

const THead = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TBody = styled.div`
  display: flex;
  flex-direction: column;
`;

const TRow = styled.div`
  display: flex;
  justify-content: space-between;

  &:hover {
    background: ${hexToRGBA(theme.colors.gray.secondary, 0.08)};
  }
`;

const TCol = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;

  border-bottom: 1px solid ${hexToRGBA(theme.colors.gray.secondary, 0.16)};
  min-height: 40px;
  padding: 4px;

  ${({ right }) =>
    right &&
    css`
      justify-content: flex-end;
    `}

  ${({ breakWords }) =>
    breakWords &&
    css`
      word-break: break-all;
      white-space: normal;
    `}

  ${({ width }) =>
    width &&
    css`
      width: ${width};
    `}

  ${({ minWidth }) =>
    minWidth &&
    css`
      min-width: ${minWidth};
    `}
`;

const Table = ({ columns = [], rows = [], loading = false, skeletonRowsNum = 4 }) => {
  const placeholderClassName = clsx('column-placeholder', { ['animated-placeholder']: loading });
  const emptyRows = useMemo(() => Array.from({ length: skeletonRowsNum }, () => ({})), [skeletonRowsNum]);

  return (
    <TWrapper>
      <THead>
        {columns.map(({ dataIndex, title, width, minWidth, right }) => (
          <TCol key={dataIndex} width={width} minWidth={minWidth} right={right}>
            <Text size={theme.fontSizes.small} weight={600}>
              {title}
            </Text>
          </TCol>
        ))}
      </THead>
      <TBody>
        {loading &&
          emptyRows.map((_, rowIdx) => (
            <TRow key={rowIdx}>
              {columns.map(({ dataIndex, width, minWidth }, colIdx) => (
                <TCol key={`${dataIndex}-${rowIdx}-${colIdx}`} width={width} minWidth={minWidth}>
                  <div className={placeholderClassName} />
                </TCol>
              ))}
            </TRow>
          ))}
        {!loading &&
          rows.map((row, idx) => (
            <TRow key={idx} className={row.classes || ''}>
              {columns.map(({ dataIndex, width, minWidth, breakWords, right, render }) => (
                <TCol
                  key={`${dataIndex}-${idx}`}
                  width={width}
                  minWidth={minWidth}
                  breakWords={breakWords}
                  right={right}
                >
                  {render ? render(row, idx) : row[dataIndex]}
                </TCol>
              ))}
            </TRow>
          ))}
      </TBody>
    </TWrapper>
  );
};

Table.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      dataIndex: PropTypes.string.isRequired,
      title: PropTypes.node,
      width: PropTypes.string,
      right: PropTypes.bool,
      render: PropTypes.func,
    })
  ),
  rows: PropTypes.array,
  loading: PropTypes.bool,
  skeletonRowsNum: PropTypes.number,
};

export default Table;
